"use client"

import { useActionState } from "react"
import Input from "@modules/common/components/input"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { signup } from "@lib/data/customer"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Register = ({ setCurrentView }: Props) => {
  const [message, formAction] = useActionState(signup, null)

  return (
    <div
      className="max-w-sm w-full flex flex-col items-center bg-surface border border-line rounded-large p-8"
      data-testid="register-page"
    >
      <span className="text-xs uppercase tracking-[0.14em] font-semibold text-clay mb-3">
        The Tile Place
      </span>
      <h1 className="font-serif text-3xl tracking-tight text-ink mb-3 text-center">
        Create your account
      </h1>
      <p className="text-center text-sm text-muted mb-6">
        Join The Tile Place to track orders, reorder your favorite tile, and
        check out faster.
      </p>
      <form className="w-full flex flex-col" action={formAction}>
        <div className="flex flex-col w-full gap-y-2">
          <Input
            label="First name"
            name="first_name"
            required
            autoComplete="given-name"
            data-testid="first-name-input"
          />
          <Input
            label="Last name"
            name="last_name"
            required
            autoComplete="family-name"
            data-testid="last-name-input"
          />
          <Input
            label="Email"
            name="email"
            required
            type="email"
            autoComplete="email"
            data-testid="email-input"
          />
          <Input
            label="Phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            data-testid="phone-input"
          />
          <Input
            label="Password"
            name="password"
            required
            type="password"
            autoComplete="new-password"
            data-testid="password-input"
          />
        </div>
        <ErrorMessage error={message} data-testid="register-error" />
        <span className="text-center text-muted text-sm mt-6">
          By creating an account, you agree to The Tile Place&apos;s{" "}
          <LocalizedClientLink
            href="/content/privacy-policy"
            className="text-clay underline transition-colors hover:text-clay-deep"
          >
            Privacy Policy
          </LocalizedClientLink>{" "}
          and{" "}
          <LocalizedClientLink
            href="/content/terms-of-use"
            className="text-clay underline transition-colors hover:text-clay-deep"
          >
            Terms of Use
          </LocalizedClientLink>
          .
        </span>
        <SubmitButton
          className="btn-clay w-full mt-6"
          data-testid="register-button"
        >
          Join
        </SubmitButton>
      </form>
      <span className="text-center text-muted text-sm mt-6">
        Already a member?{" "}
        <button
          onClick={() => setCurrentView(LOGIN_VIEW.SIGN_IN)}
          className="text-clay font-medium transition-colors hover:text-clay-deep"
        >
          Sign in
        </button>
        .
      </span>
    </div>
  )
}

export default Register
